import { Box, InputAdornment, Paper, Popper, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { Title } from '@/styles/styled';
import NoContent from '@/components/no-content';
import HighlightText from '@/components/highlight-text';
import { capitalize } from 'lodash';

const SearchDocumentSidebar = ({ sidebarData }) => {
  const { t } = useTranslation('document');
  const theme = useTheme();
  const router = useRouter();
  const [searchValue, setSearchValue] = useState(router?.query?.searchValue || '');
  const [responseSearch, setSearchResponse] = useState();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl) && !!searchValue?.trim();
  const textFieldRef = useRef(null);
  const anchorWidth = textFieldRef.current?.getBoundingClientRect().width;
  const currentService = router.asPath.split('/')[2];
  const productId = router?.query?.productId;

  const flattenSidebarData = useMemo(() => {
    return sidebarData.flatMap(
      (item) =>
        item.attributes.document_pages.data?.map((page) => {
          const breadcrumbs = [
            t('document', { ns: 'document' }),
            capitalize(productId?.[0]?.replaceAll('-', ' ')),
          ];

          breadcrumbs.push(item.attributes.name);
          breadcrumbs.push(page.attributes.title);

          return {
            title: page.attributes.title,
            slug: page.attributes.slug,
            parentSlug: item.attributes.slug,
            breadcrumbs,
          };
        }) ?? []
    );
  }, [sidebarData, productId, t]);

  const getSearchDocumentValue = (searchValueInput) => {
    const filteredData = flattenSidebarData?.filter((item) =>
      item.title?.toLowerCase().includes(searchValueInput.toLowerCase())
    );
    setSearchResponse(filteredData);
  };

  return (
    <Box sx={{ width: { xs: '100%' }, display: 'flex' }}>
      <Popper
        open={open}
        anchorEl={textFieldRef.current}
        placement="bottom-start"
        sx={{
          width: anchorWidth * 1.5,
          zIndex: 1997,
        }}
      >
        <Paper
          sx={{
            width: '100%',
            px: 1,
            py: 1,
            maxHeight: '380px',
            overflowY: 'auto',
            borderRadius: '12px',
            boxShadow: '0px 24px 40px -12px #231F202E',
          }}
        >
          {responseSearch?.length ? (
            responseSearch.map((item) => {
              return (
                <Box
                  key={item?.slug}
                  mt={2}
                  sx={{
                    transition: 'all .3s',
                    p: '10px 8px',
                    borderRadius: 1,
                    ':hover': {
                      backgroundColor: '#F2F2F2',
                      color: '#231F20',
                    },
                  }}
                >
                  <Link
                    onMouseDown={(event) => {
                      event.preventDefault();
                      setAnchorEl(null);
                      setSearchValue('');
                      router.push(`/document/${currentService}/${item.parentSlug}/${item.slug}`);
                    }}
                    href={`/document/${currentService}/${item.parentSlug}/${item.slug}`}
                  >
                    <Title
                      sx={{ marginBottom: 0, color: '#231F20', fontWeight: 500 }}
                      fontSize={16}
                    >
                      <HighlightText text={item?.title} keyword={searchValue?.trim()} />
                    </Title>
                    <div style={{ marginBottom: '4px' }} />
                    <Title
                      sx={{
                        marginBottom: 0,
                        color: '#5E5E5E',
                        fontWeight: 400,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      fontSize={14}
                    >
                      {item.breadcrumbs?.join(' / ')}
                    </Title>
                  </Link>
                </Box>
              );
            })
          ) : (
            <NoContent p={2} title={t('no_document', { ns: 'messages' })} />
          )}
        </Paper>
      </Popper>
      <TextField
        ref={textFieldRef}
        autoFocus
        onFocus={(e) => setAnchorEl(e.currentTarget)}
        onBlur={() => setAnchorEl(null)}
        size="small"
        fullWidth
        variant="outlined"
        value={searchValue}
        onChange={(e) => {
          setSearchValue(e.target.value);
          getSearchDocumentValue(e.target.value?.trim());
        }}
        sx={{
          borderRadius: '8px',
          border: `1px solid #D1D1D1`,
          backgroundColor: '#fff',
          boxShadow: '0px 1px 4px 0px #231F201A',
          '& .MuiInputBase-input': {
            fontSize: 14,
            padding: '8px 12px 8px 0px',
            borderRadius: '8px',
            backgroundColor: '#fff',
            color: theme.palette.text.black,
          },
          '& .MuiInputBase-root': {
            pr: 0.75,
            minHeight: 42,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        }}
        placeholder={t('search_document', { ns: 'document' })}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Image width={20} height={20} src="/images/search.svg" alt="search icon" />
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
};

export default SearchDocumentSidebar;
